import { Button, MenuItem, TableFooter, TablePagination } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getUserActionState } from "app/reducers/userSlice";
import { activateUser, deactivateUser, fetchUsers } from "app/thunks/userAPI";
import classNames from "classnames";
import { ConfirmationModal } from "features/confirmation-modal/ConfirmationModal";
import { NAVIGATOR } from "features/navigation/index";
import { User } from "interfaces/User";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import {
  ActivateIcon,
  DeactivateIcon,
  EditIcon,
  Title,
  useCustomToast,
} from "ui";
import { BUTTON_TYPES } from "ui/Button";
import { ColumnHeader, Table, TableBody, TableHead, TableRow } from "ui/Table";
import Search from "ui/Table/TableSearch";
import TableSelect from "ui/Table/TableSelect";
import { USERS_COLUMNS } from "utils/table/users-table";

import { UserTableBodyRow } from "./UserTableBodyRow";

const useStyles = makeStyles({
  root: {
    color: "#DADADA",
    position: "absolute",
    right: "0px",
  },
  selectIcon: {
    fill: "#DADADA",
  },
  backButtonIcon: {
    color: "#555 !important",
  },
});

const options = [
  { value: "active", label: "Activas" },
  { value: "inactive", label: "Inactivas" },
  { value: "all", label: "Todas" },
];

export const UserScreen = () => {
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const searchValue = params.get("search") || "";
  const isActiveValue = params.get("isActive") || "";

  const { users, isFetching } = useAppSelector(getUserActionState);
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateUserStatus, setUpdateUserStatus] = useState<{
    id: string;
    isActive: boolean;
  }>();

  const [isOpen, setIsOpen] = useState<string>("");

  const handleNavigation = () => {
    history.push(NAVIGATOR.createUser);
  };

  const handleChangePage = (_: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleActive = useCallback(
    (row: User) => () => {
      setUpdateUserStatus({
        id: row._id,
        isActive: row.isActive,
      });
      setIsModalOpen(true);
      setIsOpen("");
    },
    [],
  );

  const handleEdit = useCallback(
    (id: string) => {
      history.push(`${NAVIGATOR.editUser}/${id}`);
    },
    [history],
  );

  useEffect(() => {
    dispatch(
      fetchUsers({ searchParam: searchValue, isActiveParam: isActiveValue }),
    );
    setPage(0);
  }, [dispatch, isActiveValue, searchValue]);

  const actionsNode = useCallback(
    (row: User) => {
      return [
        <MenuItem
          key="toggle-active"
          divider={true}
          onClick={handleToggleActive(row)}
        >
          {row.isActive ? (
            <>
              <DeactivateIcon className="mr-2" />
              Desactivar
            </>
          ) : (
            <>
              <ActivateIcon className="mr-2" />
              Activar
            </>
          )}
        </MenuItem>,

        <MenuItem key="edit" onClick={() => handleEdit(row._id)}>
          <EditIcon className="mr-2" />
          Editar
        </MenuItem>,
      ];
    },
    [handleEdit, handleToggleActive],
  );

  const onActivateOrDeactivate = async () => {
    if (!updateUserStatus) return;
    try {
      if (updateUserStatus.isActive) {
        await dispatch(deactivateUser(updateUserStatus.id)).unwrap();
      } else {
        await dispatch(activateUser(updateUserStatus.id)).unwrap();
      }
      showSuccessToast({
        title: "¡Listo!",
        message: `La cuenta se ha ${updateUserStatus.isActive ? "desactivado" : "activado"}.`,
      });
      setIsModalOpen(false);
    } catch (_) {
      setErrorMessage();
    }
  };

  const setErrorMessage = () => {
    if (!updateUserStatus) return;
    showErrorToast({
      title: "Lo sentimos",
      message: `No pudimos ${updateUserStatus.isActive ? "desactivar" : "activar"} la cuenta`,
    });
  };

  return (
    <div className="flex flex-col justify-between w-full min-h-screen px-8 pb-8 bg-faux-dark-gunmetal pt-19">
      <div>
        <Title text="Cuentas registradas" />
        <div className="flex justify-between items-center w-full">
          <div className="flex gap-4">
            <Search placeholder="Buscar por nombre" />
            <div className="w-[150px]">
              <TableSelect options={options} placeholder="Estado" />
            </div>
          </div>
          <div className="mb-5">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => handleNavigation()}
            >
              Crear cuenta
            </Button>
          </div>
        </div>
        <Table>
          <TableHead>
            <TableRow>
              {USERS_COLUMNS.map((column, index) => (
                <ColumnHeader
                  key={index}
                  className={classNames(
                    index === 0 && "pl-2",
                    index === USERS_COLUMNS.length - 1 && "pr-2",
                  )}
                >
                  {column.title}
                </ColumnHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            isLoading={isFetching}
            isEmpty={!users?.length}
            columnAmount={USERS_COLUMNS.length + 1}
          >
            {(rowsPerPage > 0
              ? users.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : users
            ).map((user: User) => (
              <UserTableBodyRow
                key={user._id}
                user={user}
                actionsNode={actionsNode}
                isOpen={isOpen === user._id}
                setIsOpen={setIsOpen}
              />
            ))}
          </TableBody>
          <TableFooter>
            <TableRow className="relative h-[53px]">
              <TablePagination
                classes={{ ...classes }}
                colSpan={3}
                count={users.length}
                rowsPerPageOptions={[]}
                rowsPerPage={rowsPerPage}
                page={page}
                slotProps={{
                  actions: {
                    previousButton: {
                      classes: {
                        disabled: classes.backButtonIcon,
                      },
                    },
                    nextButton: {
                      classes: {
                        disabled: classes.backButtonIcon,
                      },
                    },
                  },
                }}
                labelRowsPerPage=""
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
        {isModalOpen && (
          <ConfirmationModal
            closeButtonType={BUTTON_TYPES.CANCEL}
            confirmButtonType={BUTTON_TYPES.CONFIRM}
            closeText="Cancelar"
            confirmText={updateUserStatus?.isActive ? "Desactivar" : "Activar"}
            title={
              updateUserStatus?.isActive
                ? "Desactivar Cuenta"
                : "Activar Cuenta"
            }
            label={`Vas a ${updateUserStatus?.isActive ? "desactivar" : "activar"} esta cuenta, ¿quieres continuar?`}
            callback={onActivateOrDeactivate}
            callbackClose={() => setIsModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
};
